import { forwardRef, useEffect, useMemo, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import { styled } from "@mui/material";
import { FormCPFMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_cpf_masked_input";
import { FormMoneyMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_money_masked_input";
import { FormMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_masked_input";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { StyledDataGrid } from "../../../../../../common/components/styled_data_grid";
import {
  GenerateCentroCustoSecundarioColumnsDefinition,
  GenerateDocumentoColumnsDefinition,
} from "../columns";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { FormRadioGroupInput } from "../../../../../../common/components/masked_inputs/react_forms/form_radio_group_input";
import {
  useCreateDebounceHandler,
  useDebouncedCallback,
} from "../../../../../../common/hooks/use_debounced_callback";
import { CentroCustoResume } from "../../../../../../types/comercial/centroCusto";

import DefaultFormInput from "../../../../../../common/components/default_form_input";
import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import CommonConstants from "../../../../../../common/constants";
import { DropDownCargo } from "../../../../../../common/components/dropdown_cargo";
import { CentroCustoSecundario } from "../../../../../rh/domain/models/centro_custo_secundario";
import { Cep } from "../../../../../rh/domain/models/cep";
import { FuncionarioTercForm } from "../../../schemas/Funcionario_Terc/funcionario_ter";
import { DropDownFornecedor } from "../../../../../../common/components/dropdown_fornecedor";
import { EnderecoForm } from "../../../schemas/Funcionario_Terc/endereco";
import { FuncionarioTercLista } from "../../../../domain/models/funcionarios_tercerizado_lista";
import { Documento } from "../../../../../rh/domain/models/documento";
import { FolderOpen, Launch } from "@mui/icons-material";
import { ActionBanner } from "../../../../../../common/components/ActionBanner";

/**
 * VisuallyHiddenInput
 */

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});


type Props = {
  loading: boolean;
  isCreated: boolean;
  funcionario: FuncionarioTercLista | null | undefined;
  fileUploadUrl: string | null;
  centroCustoLista: CentroCustoDropDown[];
  hookForm: UseFormReturn<FuncionarioTercForm>;
  hookFormEndereco: UseFormReturn<EnderecoForm>;

  centroCustoSecundarioLista: CentroCustoResume[];
  DocumetosLista: Documento[];

  onSetCadastroCentroCustoSecundarioOpenState: (
    id: number | null,
    openState: boolean,
  ) => void;
  onStartDeleteCentroCustoSecundario: (id: number, openState: boolean) => void;

  onSetCadastroDocumentosOpenState: (
    id: number | null,
    openState: boolean,
  ) => void;
  onSetDocumentosOpenState: (id: number | null, openState: boolean) => void;
  onStartDeleteDocumentos: (id: number, openState: boolean) => void;

  onStartFileUpload: (file: File | null) => void;
  onDeleteFileUpload: () => void;
  onLoadCep: (cep: string) => Promise<Cep | undefined>;
  onDownload: (idDocumento: number, imageName: string) => void;
};

export const TabInformacoesGeraisContent = forwardRef<HTMLButtonElement, Props>(
  (
    {
      loading,
      isCreated,
      funcionario,
      fileUploadUrl,
      centroCustoLista,
      hookForm,
      hookFormEndereco,
      onDownload,
      centroCustoSecundarioLista,
      DocumetosLista,
      onSetDocumentosOpenState,

      onSetCadastroCentroCustoSecundarioOpenState,
      onStartDeleteCentroCustoSecundario,

      onSetCadastroDocumentosOpenState,
      onStartDeleteDocumentos,

      onStartFileUpload,
      onDeleteFileUpload,
      onLoadCep,
    },
    ref,
  ) => {
    const onLoadCepSkiper = useRef(false);
    const debounceHandler = useCreateDebounceHandler(750);

    const centroCustoSecundarioColumnsDefinition = useMemo(() => {
      return GenerateCentroCustoSecundarioColumnsDefinition({
        handleActionEdit: (idCentroCustoSecundario) =>
          onSetCadastroCentroCustoSecundarioOpenState(
            idCentroCustoSecundario,
            true,
          ),
        handleActionDelete: (idCentroCustoSecundario) =>
          onStartDeleteCentroCustoSecundario(idCentroCustoSecundario, true),
      });
    }, [
      onSetCadastroCentroCustoSecundarioOpenState,
      onStartDeleteCentroCustoSecundario,
    ]);

    const centroDocumentosColumnsDefinition = useMemo(() => {
      return GenerateDocumentoColumnsDefinition({
        onActionEdit: (idDocumento) =>
          onSetCadastroDocumentosOpenState(Number(idDocumento), true),
        onActionDelete: (idDocumento) =>
          onStartDeleteDocumentos(Number(idDocumento), true),
        onDownload: (idDocumento, imageName) =>
          onDownload(idDocumento, imageName),
      });
    }, [onDownload, onSetCadastroDocumentosOpenState, onStartDeleteDocumentos]);

    const loadCep = useDebouncedCallback(
      debounceHandler,
      async (cep: string) => {
        return async () => {
          if (typeof cep === "string") {
            const normalizedCep = cep.trim();
            const requiredSize = "xx.xxx-xxx";

            if (normalizedCep.length === requiredSize.length) {
              const cepInfo = await onLoadCep(cep);
              if (cepInfo) {
                hookFormEndereco.setValue(
                  "Rua",
                  cepInfo.logradouro.toUpperCase(),
                );
                hookFormEndereco.setValue(
                  "Bairro",
                  cepInfo.bairro.toUpperCase(),
                );
                hookFormEndereco.setValue(
                  "Cidade",
                  cepInfo.cidade.toUpperCase(),
                );
                hookFormEndereco.setValue("Uf", cepInfo.uf.toUpperCase());
                hookFormEndereco.setValue("Pais", cepInfo.pais.toUpperCase());
              }
            } else if (normalizedCep.length === 0) {
              hookFormEndereco.setValue("Rua", "");
              hookFormEndereco.setValue("Bairro", "");
              hookFormEndereco.setValue("Cidade", "");
              hookFormEndereco.setValue("Uf", "");
              hookFormEndereco.setValue("Pais", "");
            }
          }
        };
      },
      [hookForm, onLoadCep],
    );

    useEffect(() => {
      if (funcionario) {
        onLoadCepSkiper.current = true;
      }
    }, [funcionario]);

    const tabelaCentroCustoSecundario = useMemo(() => {
      return (
        <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 p-4 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode mb-4">
          <StyledDataGrid
            tableName="tab_informacoes_gerais_content_1"
            disableColumnMenu={false}
            getRowId={(row: CentroCustoSecundario) =>
              row.idCentroCustoSecundario
            }
            columns={centroCustoSecundarioColumnsDefinition}
            loading={loading}
            height="35vh"
            rows={centroCustoSecundarioLista}
            rowCount={centroCustoSecundarioLista.length}
          />
        </div>
      );
    }, [
      loading,
      centroCustoSecundarioLista,
      centroCustoSecundarioColumnsDefinition,
    ]);

    return (
      <>
        <>
          <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 p-4 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode mb-4">
            <FormContainer title={"Foto 3x4"} arrangement="fullRow">
              <>
                <div className="w-[16rem] h-[12rem] border border-gray-300 py-2 px-6">
                  <div
                    className="w-full h-full"
                    onClick={() =>
                      document.getElementById("profileInput")?.click()
                    }
                  >
                    {fileUploadUrl ? (
                      <img
                        src={fileUploadUrl}
                        className="w-full h-full object-contain bg-center bg-no-repeat"
                        alt="User profile"
                      />
                    ) : funcionario &&
                      funcionario.Imagem &&
                      funcionario.Imagem.length > 0 ? (
                      <img
                        src={`data:image/*; base64,${funcionario.Imagem}`}
                        className="w-full h-full object-contain bg-center bg-no-repeat"
                        alt=""
                      />
                    ) : (
                      <img
                        src={CommonConstants.assets.noAvatar}
                        className="w-full h-full object-contain bg-center bg-no-repeat"
                        alt=""
                      />
                    )}
                  </div>
                </div>
              </>
            </FormContainer>
            <div className="w-full flex flex-row justify-between mt-2">
              <p className="flex-1 text-black dark:text-white">
                São permitidas apenas imagens com extensão .png, .jpeg, .jpg,
                .gif, .img!
              </p>
              <div className="flex flex-row gap-2">
                <PrimaryButton
                  component="label"
                  id="profileInput"
                  sx={{ paddingLeft: 4, paddingRight: 4 }}
                >
                  <VisuallyHiddenInput
                    type="file"
                    id="profileImageInput"
                    onChange={(e) => {
                      onStartFileUpload(e.target.files?.item(0) ?? null);
                    }}
                    accept=".png,.jpeg,.jpg,.gif,.img"
                    multiple={false}
                  />
                  Selecionar
                </PrimaryButton>
                <PrimaryButton
                  onClick={() => onDeleteFileUpload()}
                  buttonColor="red"
                >
                  Excluir
                </PrimaryButton>
              </div>
            </div>
            <FormRadioGroupInput
              name="Ativo"
              label="Ativo?"
              options={[
                { id: 0, label: "Não" },
                { id: 1, label: "Sim" },
              ]}
              reactHookForm={hookForm}
            />
          </div>
        </>
        <>
          <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 p-4 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode mb-4">
            <FormContainer title={"Informações Pessoais"}>
              <>
                <DefaultFormInput
                  name={"Nome"}
                  label={"Nome"}
                  required={true}
                  reactHookForm={hookForm}
                />

                <DefaultFormAutocomplete
                  name="IdCentroCusto"
                  label="Centro de Custo"
                  required={true}
                  newButtonLabel="Novo"
                  getOptionLabel={(x) => x.title}
                  getOptionKey={(x) => x.id}
                  options={centroCustoLista}
                  reactHookForm={hookForm}
                  onNew={() => {}}
                />

                <DropDownCargo
                  name="IdCargo"
                  required={true}
                  reactHookForm={hookForm}
                />
                <FormDatePickerInput
                  name="DataNascimento"
                  label="Data de nascimento"
                  reactHookForm={hookForm}
                />
                <DropDownFornecedor
                  name="IdFornecedor"
                  label="Fornecedor"
                  reactHookForm={hookForm}
                />
                <FormDatePickerInput
                  name="DataInicio"
                  label="Data Inicio"
                  reactHookForm={hookForm}
                />

                <FormDatePickerInput
                  name="DataTermino"
                  label="Data Termino"
                  reactHookForm={hookForm}
                />

                <FormRadioGroupInput
                  name="StatusAso"
                  label="O ASO está conforme o PCMSO?"
                  options={[
                    { id: 1, label: "Não" },
                    { id: 0, label: "Sim" },
                    { id: 2, label: "Não Se Aplica" },
                  ]}
                  reactHookForm={hookForm}
                />

                <FormMoneyMaskedInput
                  name="Salario"
                  label={"Salário"}
                  reactHookForm={hookForm}
                />
              </>
            </FormContainer>

            <FormContainer title={"CTPS"}>
              <>
                <FormCPFMaskedInput
                  name="Cpf"
                  label="CPF"
                  reactHookForm={hookForm}
                />
                <DefaultFormInput
                  name={"Ctps"}
                  label={"CTPS"}
                  reactHookForm={hookForm}
                />
                <DefaultFormInput
                  name={"Serie"}
                  label={"Série"}
                  reactHookForm={hookForm}
                />

                <DefaultFormInput
                  name={"CtpsUf"}
                  label={"UF"}
                  reactHookForm={hookForm}
                />

                <FormDatePickerInput
                  name="DataEmissao"
                  label="Data Emissão"
                  reactHookForm={hookForm}
                />
              </>
            </FormContainer>

            <FormContainer title={"RG"}>
              <>
                <DefaultFormInput
                  name={"Rg"}
                  label={"Número"}
                  reactHookForm={hookForm}
                />

                <FormDatePickerInput
                  name="DataExpedicao"
                  label="Data de Expedição"
                  reactHookForm={hookForm}
                />

                <DefaultFormInput
                  name={"OrgExp"}
                  label={"Órgão Exped."}
                  reactHookForm={hookForm}
                />

                <DefaultFormInput
                  name={"RgUf"}
                  label={"UF"}
                  reactHookForm={hookForm}
                />
              </>
            </FormContainer>
          </div>
        </>
        <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 p-4 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode mb-4">
          <FormContainer title={"Endereço"}>
            <>
              <FormMaskedInput
                name="Cep"
                label={"CEP"}
                unmask={false}
                mask="00.000-000"
                maskPlaceholder="__.___-___"
                reactHookForm={hookFormEndereco}
                onChange={(cep) => {
                  const normalizedCep = cep.trim().replaceAll("_", "");
                  const requiredSize = "xx.xxx-xxx";

                  if (!onLoadCepSkiper.current) {
                    if (
                      normalizedCep.length === 0 ||
                      normalizedCep.length === requiredSize.length
                    ) {
                      loadCep(cep);
                    }
                  }
                  onLoadCepSkiper.current = false;
                }}
              />

              <DefaultFormInput
                name={"Cidade"}
                label={"Cidade"}
                reactHookForm={hookFormEndereco}
              />

              <DefaultFormInput
                name={"Rua"}
                label={"Endereço"}
                reactHookForm={hookFormEndereco}
              />

              <DefaultFormInput
                name={"Numero"}
                label={"Número"}
                reactHookForm={hookFormEndereco}
              />

              {/* <DefaultFormInput
            name={"Complemento"}
            label={"Complemento"}
            reactHookForm={hookFormEndereco}
          /> */}

              <DefaultFormInput
                name={"Bairro"}
                label={"Bairro"}
                reactHookForm={hookFormEndereco}
              />

              <DefaultFormInput
                name={"Uf"}
                label={"Estado"}
                reactHookForm={hookFormEndereco}
              />

              <DefaultFormInput
                name={"Pais"}
                label={"País"}
                reactHookForm={hookFormEndereco}
              />
            </>
          </FormContainer>
        </div>
        <>
          <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 p-4 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode mb-4">
            <FormContainer title={"Observações"}>
              <>
                <div className="col-span-2">
                  <DefaultFormInput
                    name={"Observacoes"}
                    label={"Observações"}
                    multiline={true}
                    rows={7}
                    reactHookForm={hookForm}
                  />
                </div>
              </>
            </FormContainer>
          </div>
          {isCreated && (
            <>
              <div className="w-full flex flex-row justify-between items-center mt-8 mb-2">
                <h1 className="font-bold text-black dark:text-white text-3xl">
                  Centro de Custo Secundários
                </h1>
                <div>
                  <PrimaryButton
                    startIcon={
                      <DynamicIcon
                        nameIcon="AddRounded"
                        size={25}
                        className="text-white"
                      />
                    }
                    sx={{ px: 2 }}
                    onClick={() =>
                      onSetCadastroCentroCustoSecundarioOpenState(null, true)
                    }
                  >
                    Novo
                  </PrimaryButton>
                </div>
              </div>
              {tabelaCentroCustoSecundario}
            </>
          )}

          {isCreated && (
            <>
              <ActionBanner
                icon={<FolderOpen style={{ fontSize: 24 }} />}
                title="Documentos Diversos"
                description="Gerencie os arquivos e anexos relacionados a este registro."
                buttonIcon={<Launch style={{ fontSize: 18 }} />}
                buttonText="Abrir Painel de Documentos"
                onClick={() => onSetDocumentosOpenState(null, true)}
              />
            </>
          )}
        </>
      </>
    );
  },
);
